import { createFileRoute, useSearch, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, Play, Copy, Trash2, Save, History } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { Card } from "@/components/ui/card";
import { useScope } from "@/hooks/use-scope";

import { ConditionBuilder } from "@/components/rules/condition-builder";
import { ActionList } from "@/components/rules/action-list";
import { TRIGGERS } from "@/lib/rules/registry";
import { describe } from "@/lib/rules/evaluator";
import type { Action, Condition, RuleScope, TriggerEvent } from "@/lib/rules/types";
import {
  deleteRule, duplicateRule, dryRunRule, listRuleRuns, listRules,
  toggleRule, upsertRule,
} from "@/lib/rules.functions";
import { listCustomersOptions } from "@/lib/admin-config.functions";

export const Route = createFileRoute("/_authenticated/app/admin/rules")({
  component: RulesAdmin,
  validateSearch: (search: Record<string, unknown>) => ({
    edit: typeof search.edit === "string" ? search.edit : undefined,
  }),
});

type Draft = {
  id?: string;
  tenant_id: string;
  name: string;
  description: string;
  is_active: boolean;
  priority: number;
  scope: RuleScope;
  facility_id: string | null;
  customer_id: string | null;
  trigger_event: TriggerEvent;
  condition_tree: Condition;
  actions: Action[];
  test_mode: boolean;
};

function blankDraft(tenantId: string): Draft {
  return {
    tenant_id: tenantId,
    name: "",
    description: "",
    is_active: true,
    priority: 100,
    scope: "tenant",
    facility_id: null,
    customer_id: null,
    trigger_event: "order.imported",
    condition_tree: { type: "and", children: [] },
    actions: [],
    test_mode: false,
  };
}

function RulesAdmin() {
  const { tenants, tenantId: scopeTenantId } = useScope();
  const [tenantId, setTenantId] = useState<string>("");
  useEffect(() => {
    if (!tenantId && scopeTenantId) setTenantId(scopeTenantId);
    else if (!tenantId && tenants[0]) setTenantId(tenants[0].id);
  }, [tenantId, scopeTenantId, tenants]);

  const [editing, setEditing] = useState<Draft | null>(null);
  const { edit: editParam } = useSearch({ from: "/_authenticated/app/admin/rules" });
  const navigate = useNavigate();

  const list = useServerFn(listRules);
  const save = useServerFn(upsertRule);
  const toggle = useServerFn(toggleRule);
  const dup = useServerFn(duplicateRule);
  const del = useServerFn(deleteRule);
  const dry = useServerFn(dryRunRule);
  const runsFn = useServerFn(listRuleRuns);
  const customersFn = useServerFn(listCustomersOptions);
  const qc = useQueryClient();

  const rulesQ = useQuery({
    queryKey: ["rules", tenantId],
    queryFn: () => list({ data: { tenantId } }),
    enabled: !!tenantId,
  });

  const runsQ = useQuery({
    queryKey: ["rules", "runs", tenantId],
    queryFn: () => runsFn({ data: { tenantId, limit: 100 } }),
    enabled: !!tenantId,
  });

  const customersQ = useQuery({
    queryKey: ["customers-options"],
    queryFn: () => customersFn(),
  });
  const customers = ((customersQ.data ?? []) as { id: string; name: string; tenant_id: string }[])
    .filter((c) => !tenantId || c.tenant_id === tenantId);
  const customerName = (id: string | null | undefined) =>
    customers.find((c) => c.id === id)?.name ?? null;

  const refresh = () => qc.invalidateQueries({ queryKey: ["rules"] });

  // Open the editor when navigated here with ?edit=<ruleId> (from Policy Library).
  useEffect(() => {
    if (!editParam || editing) return;
    const rules = (rulesQ.data as { rules?: any[] } | undefined)?.rules ?? [];
    const found = rules.find((r) => r.id === editParam);
    if (found) {
      setEditing({ ...found, description: found.description ?? "" } as Draft);
      navigate({ to: "/app/admin/rules", search: {}, replace: true });
    }
  }, [editParam, editing, rulesQ.data, navigate]);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => save({ data: d as never }),
    onSuccess: (r) => {
      toast.success("Rule saved");
      setEditing(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dryMut = useMutation({
    mutationFn: (d: Draft) =>
      dry({ data: { tenantId, draft: d as never, sampleSize: 10 } }),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Automation Rules"
        description="IF / AND / OR / THEN rules that fire automatically on order, pick, pack, and shipment events. Test in dry-run before enabling."
        actions={
          <div className="flex items-center gap-2">
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger className="w-64"><SelectValue placeholder="Tenant" /></SelectTrigger>
              <SelectContent>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => tenantId && setEditing(blankDraft(tenantId))} disabled={!tenantId}>
              <Plus className="h-4 w-4 mr-1" /> New rule
            </Button>
          </div>
        }
      />

      {editing ? (
        <RuleEditor
          draft={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => saveMut.mutate(editing)}
          saving={saveMut.isPending}
          onDryRun={() => dryMut.mutate(editing)}
          dryResult={(dryMut.data as { previews?: any[] } | undefined)?.previews ?? null}
          dryRunning={dryMut.isPending}
          customers={customers}
        />
      ) : (
        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules">Rules</TabsTrigger>
            <TabsTrigger value="history">Execution history</TabsTrigger>
          </TabsList>

          <TabsContent value="rules">
            <Card className="divide-y">
              {rulesQ.isLoading && (
                <div className="p-6 flex justify-center"><Loader2 className="animate-spin h-5 w-5 text-muted-foreground" /></div>
              )}
              {rulesQ.data?.rules?.length === 0 && (
                <div className="p-6 text-sm text-muted-foreground text-center">No rules yet. Create one to get started.</div>
              )}
              {(rulesQ.data?.rules ?? []).map((r: any) => (
                <div key={r.id} className="p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.name}</span>
                      <Badge variant="outline">{r.trigger_event}</Badge>
                      <Badge variant="secondary">
                        scope: {r.scope}
                        {r.scope === "customer" && r.customer_id
                          ? ` · ${customerName(r.customer_id) ?? r.customer_id.slice(0, 8)}`
                          : ""}
                      </Badge>
                      <Badge variant="outline">priority {r.priority}</Badge>
                      {r.test_mode && <Badge variant="outline">test mode</Badge>}
                      {!r.is_active && <Badge variant="destructive">disabled</Badge>}
                    </div>
                    {r.description && <p className="text-xs text-muted-foreground mt-1">{r.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1 font-mono">
                      IF {describe(r.condition_tree)} THEN {(r.actions ?? []).map((a: Action) => a.kind).join(", ") || "(none)"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={r.is_active}
                      onCheckedChange={async (v) => {
                        await toggle({ data: { id: r.id, is_active: v } });
                        refresh();
                      }}
                    />
                    <Button size="sm" variant="ghost"
                      onClick={() => setEditing({ ...r, description: r.description ?? "" } as Draft)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={async () => { await dup({ data: { id: r.id } }); refresh(); }}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={async () => {
                        if (!confirm("Delete this rule?")) return;
                        await del({ data: { id: r.id } });
                        refresh();
                      }}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="divide-y">
              {runsQ.isLoading && (
                <div className="p-6 flex justify-center"><Loader2 className="animate-spin h-5 w-5 text-muted-foreground" /></div>
              )}
              {(runsQ.data?.runs ?? []).length === 0 && (
                <div className="p-6 text-sm text-muted-foreground text-center">No rule executions yet.</div>
              )}
              {(runsQ.data?.runs ?? []).map((run: any) => (
                <div key={run.id} className="p-3 text-sm flex items-start gap-3">
                  <History className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{run.automation_rules?.name ?? "(deleted rule)"}</span>
                      <Badge variant="outline">{run.trigger_event}</Badge>
                      {run.matched
                        ? <Badge variant="secondary">matched</Badge>
                        : <Badge variant="outline">no match</Badge>}
                      {run.test_mode && <Badge variant="outline">dry-run</Badge>}
                      {run.error && <Badge variant="destructive">error</Badge>}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(run.created_at).toLocaleString()} · {run.duration_ms}ms
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
                      entity {run.entity_type}/{(run.entity_id ?? "").slice(0, 8)} ·
                      {" "}{(run.actions_applied ?? []).map((a: any) => `${a.kind}${a.ok ? "✓" : "✗"}`).join(" ") || "no actions"}
                    </p>
                    {run.error && <p className="text-xs text-destructive mt-1">{run.error}</p>}
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function RuleEditor({
  draft, onChange, onCancel, onSave, saving, onDryRun, dryRunning, dryResult, customers,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  onDryRun: () => void;
  dryRunning: boolean;
  dryResult: any[] | null;
  customers: { id: string; name: string }[];
}) {
  const summary = useMemo(() => describe(draft.condition_tree), [draft.condition_tree]);
  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="font-medium">{draft.id ? "Edit rule" : "New rule"}</h3>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="outline" onClick={onDryRun} disabled={dryRunning}>
            {dryRunning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
            Dry run
          </Button>
          <Button onClick={onSave} disabled={saving || !draft.name}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-xs text-muted-foreground">Name</label>
          <Input value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Priority (lower runs first)</label>
          <Input type="number" value={draft.priority}
            onChange={(e) => onChange({ ...draft, priority: Number(e.target.value) || 0 })} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-muted-foreground">Description</label>
          <Textarea value={draft.description}
            onChange={(e) => onChange({ ...draft, description: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Trigger</label>
          <Select value={draft.trigger_event}
            onValueChange={(v) => onChange({ ...draft, trigger_event: v as TriggerEvent, actions: [] })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Scope</label>
          <Select value={draft.scope}
            onValueChange={(v) => onChange({ ...draft, scope: v as RuleScope })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant">Tenant (all)</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="facility">Facility</SelectItem>
              <SelectItem value="global">Global</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {draft.scope === "customer" && (
          <div>
            <label className="text-xs text-muted-foreground">Customer</label>
            <Select
              value={draft.customer_id ?? ""}
              onValueChange={(v) => onChange({ ...draft, customer_id: v || null })}
            >
              <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.length === 0
                  ? <SelectItem value="__none" disabled>No customers in this tenant</SelectItem>
                  : customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {draft.scope === "facility" && (
          <div>
            <label className="text-xs text-muted-foreground">Facility ID</label>
            <Input value={draft.facility_id ?? ""}
              onChange={(e) => onChange({ ...draft, facility_id: e.target.value || null })} />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Switch checked={draft.is_active}
            onCheckedChange={(v) => onChange({ ...draft, is_active: v })} />
          <span className="text-sm">Active</span>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={draft.test_mode}
            onCheckedChange={(v) => onChange({ ...draft, test_mode: v })} />
          <span className="text-sm">Test mode (log only, no actions)</span>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-1">IF (conditions)</h4>
        <ConditionBuilder value={draft.condition_tree}
          onChange={(t) => onChange({ ...draft, condition_tree: t })} />
        <p className="text-xs text-muted-foreground mt-1 font-mono">
          IF {summary}
        </p>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-1">THEN (actions)</h4>
        <ActionList trigger={draft.trigger_event} value={draft.actions}
          onChange={(a) => onChange({ ...draft, actions: a })} />
      </div>

      {dryResult && (
        <Card className="p-3 space-y-1 bg-muted/30">
          <p className="text-sm font-medium">Dry-run preview ({dryResult.length} orders sampled)</p>
          {dryResult.length === 0 && <p className="text-xs text-muted-foreground">No orders to preview.</p>}
          {dryResult.map((p) => (
            <p key={p.orderId} className="text-xs font-mono">
              {p.orderId.slice(0, 8)} → matched {p.summary.matched}/{p.summary.evaluated} ·
              {" "}actions {p.summary.results.flatMap((r: any) => r.actions.map((a: any) => a.kind)).join(",") || "—"}
            </p>
          ))}
        </Card>
      )}
    </Card>
  );
}